import { useEffect, useState } from 'react';
import { fetchInboxAgencyTasks } from './api';
import type { AgencyTaskView } from '@tsr/shared-types';
import { isTerminalAgencyTaskStatus } from '@tsr/shared-types';
import { agencyTaskStatusBadge } from './agencyTaskUi';

/**
 * INBOX tasks worklist (#379, Coordination Re-Arch Phase 3b-2b) — the 5th Coordination
 * sub-tab. Lists the agency tasks ADDRESSED TO this agency (target-scoped on the server);
 * a row opens the {kind:'agencyTask', viewerRole:'target'} SidePanel detail, where the
 * tasked agency drives acknowledge / accept / decline / start / complete. Mirrors
 * OutboundTasksPanel: a passive 30 s re-poll + an open-count badge that stays live while
 * another tab is active. (The two-party message thread is the optional 3b-2c follow-up.)
 *
 * DEMO NOTE: the inbox filters by the ACTING agency, so under the single-operator demo
 * (operator = the PEOC/requester) this list is empty — the round-trip is proven by the
 * route e2e acting as the target; a live eyeball awaits the multi-agency / portal work.
 */

export const INBOX_TASKS_POLL_MS = 30_000;

export function InboxTasksPanel({
  onSelectTask,
  onActiveCountChange,
}: {
  onSelectTask?: (task: AgencyTaskView) => void;
  onActiveCountChange?: (count: number) => void;
}): JSX.Element {
  const [tasks, setTasks] = useState<AgencyTaskView[]>([]);

  useEffect(() => {
    let cancelled = false;
    async function reload(): Promise<void> {
      try {
        const list = await fetchInboxAgencyTasks();
        if (!cancelled) setTasks(list);
      } catch {
        /* keep current */
      }
    }
    void reload();
    const id = setInterval(() => void reload(), INBOX_TASKS_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // Report the OPEN (non-terminal) count up from a single pure effect.
  useEffect(() => {
    onActiveCountChange?.(tasks.filter((t) => !isTerminalAgencyTaskStatus(t.task_status)).length);
  }, [tasks, onActiveCountChange]);

  return (
    <div className="px-3 py-3" data-testid="inbox-tasks-panel">
      {/* #379 (agency-task inbox) — internal task number kept in the comment, off the
          operator surface (W6 raw-enum legibility, ledger §3.D). */}
      <p className="mb-2 text-[11px] leading-relaxed text-slate-400">
        Tasks another agency issued DOWN to yours. Open a row to acknowledge, accept or decline
        it, then mark it started and complete as you work it.
      </p>
      {tasks.length === 0 ? (
        <p className="text-[11px] text-slate-500">
          No tasks addressed to your agency. Tasks others issue to you arrive here.
        </p>
      ) : (
        <ul className="space-y-2">
          {tasks.map((t) => {
            const badge = agencyTaskStatusBadge(t.task_status);
            return (
              <li
                key={t.id}
                className="rounded border border-slate-700/60 bg-slate-800/40 px-3 py-2"
                data-testid="inbox-task-row"
              >
                <div className="flex items-start justify-between gap-2">
                  {onSelectTask ? (
                    <button
                      type="button"
                      onClick={() => onSelectTask(t)}
                      data-testid="inbox-task-open"
                      className="min-w-0 text-left rounded -mx-1 px-1 hover:bg-slate-700/40"
                      title="Open the agency task to respond"
                    >
                      <p className="text-sm font-medium truncate">
                        {t.requesting_agency_name ?? 'Agency'}
                      </p>
                      <p className="text-[11px] text-slate-400 truncate">
                        {t.task_number} · {t.urgency}
                      </p>
                    </button>
                  ) : (
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">
                        {t.requesting_agency_name ?? 'Agency'}
                      </p>
                      <p className="text-[11px] text-slate-400 truncate">
                        {t.task_number} · {t.urgency}
                      </p>
                    </div>
                  )}
                  <span
                    className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded font-medium ${badge.cls}`}
                  >
                    {badge.label}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
